import { RotateCw } from '@music-vine/cadence/icons';
import { Button } from '@music-vine/cadence/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { clearVideoKeepOpenAtom, videoElementAtom, videoPlaybackAtom, videoResetPointAtom } from '../atoms/videoPlayerAtoms';

/**
 * Format time in seconds to MM:SS format
 */
const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) {
    return '0:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Video player controls component
 * Provides seek and reset point controls with draggable reset marker
 */
export const VideoControls = () => {
  const videoElement = useAtomValue(videoElementAtom);
  const {
    currentTime,
    duration
  } = useAtomValue(videoPlaybackAtom);
  const resetPoint = useAtomValue(videoResetPointAtom);
  const setResetPoint = useSetAtom(videoResetPointAtom);
  const clearVideo = useSetAtom(clearVideoKeepOpenAtom);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDraggingResetRef = useRef(false);

  // Calculate reset point position as percentage
  const resetPointPercent = duration > 0 ? resetPoint / duration * 100 : 0;

  // Handle dragging the reset point marker
  const handleResetMarkerMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    isDraggingResetRef.current = true;
    const updateResetPoint = (clientX: number) => {
      if (!(sliderRef.current && videoElement) || duration === 0) {
        return;
      }
      const rect = sliderRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percent = x / rect.width;
      const newResetPoint = percent * duration;

      // Update reset point and video position for real-time preview
      setResetPoint(newResetPoint);
      videoElement.currentTime = newResetPoint;
    };
    const handleMouseMove = (e: MouseEvent) => {
      updateResetPoint(e.clientX);
    };
    const handleMouseUp = () => {
      isDraggingResetRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [videoElement, duration, setResetPoint]);

  // Handle clicking on the slider to set the reset point
  const handleSliderClick = useCallback((e: React.MouseEvent) => {
    if (!(sliderRef.current && videoElement) || duration === 0) {
      return;
    }
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percent = x / rect.width;
    const newResetPoint = percent * duration;
    setResetPoint(newResetPoint);
    videoElement.currentTime = newResetPoint;
  }, [videoElement, duration, setResetPoint]);
  return <div className="mt-4 space-y-6" data-sentry-component="VideoControls" data-sentry-source-file="VideoControls.tsx">
      {/* Header with title and change video button */}
      <div className="flex items-center justify-between">
        <h3 className="font-sans font-semibold @[420px]:text-base text-gray-900 text-sm dark:text-gray-100">
          Select sync point
        </h3>
        <Button className="shrink-0 gap-0" onClick={() => clearVideo()} size="sm" variant="subtle" data-sentry-element="Button" data-sentry-source-file="VideoControls.tsx">
          <RotateCw className="mr1 size-3 fill-none text-pink-600 dark:text-white" data-sentry-element="RotateCw" data-sentry-source-file="VideoControls.tsx" />
          <span className="ml-1 text-pink-600 dark:text-white">
            Change video
          </span>
        </Button>
      </div>

      {/* Progress bar with reset point marker */}
      <div className="space-y-1">
        <div className="relative h-1 w-full cursor-pointer rounded-full bg-gray-300 dark:bg-gray-700" onClick={handleSliderClick} ref={sliderRef}>
          {/* Progress fill */}
          <div className="h-full rounded-full bg-pink-500" style={{
          width: `${duration > 0 ? currentTime / duration * 100 : 0}%`
        }} />

          {/* Draggable reset point marker */}
          <div className="pointer-events-auto absolute cursor-pointer" onMouseDown={handleResetMarkerMouseDown} style={{
          left: `${resetPointPercent}%`,
          top: '-28px',
          transform: 'translateX(-50%)'
        }} title={`Reset Point: ${formatTime(resetPoint)} (drag to adjust)`}>
            {/* Dashed line connecting marker to bar */}
            <div className="absolute h-2 w-px border-0 border-gray-800 border-r-1 border-dashed dark:border-gray-100" style={{
            left: '10px',
            top: '20px'
          }} />

            <svg aria-hidden="true" fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="VideoControls.tsx">
              <path className="fill-gray-800 stroke-gray-800 dark:fill-gray-100 dark:stroke-gray-100" d="M6.24107 12.2292C6.241 12.5475 6.36737 12.8528 6.59238 13.078L9.63571 16.1218C9.86079 16.3469 10.1661 16.4734 10.4844 16.4734C10.8028 16.4734 11.1081 16.347 11.3332 16.1219L14.377 13.0786C14.602 12.8535 14.7284 12.5482 14.7284 12.2299L14.729 4.84249C14.7278 4.45795 14.5745 4.08949 14.3026 3.81756C14.0308 3.54562 13.6623 3.39229 13.2778 3.39105L7.6931 3.39061C7.30856 3.3918 6.9401 3.54507 6.66816 3.81696C6.39623 4.08885 6.2429 4.45729 6.24165 4.84183L6.24107 12.2292Z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.66667" data-sentry-element="path" data-sentry-source-file="VideoControls.tsx" />
            </svg>
          </div>
        </div>

        {/* Time display */}
        <div className="grid grid-cols-3 gap-2 text-gray-700 text-xs tabular-nums dark:text-gray-200">
          <span className="text-left">{formatTime(currentTime)}</span>
          <span className="text-center text-gray-700 dark:text-gray-200">
            Track starts at{' '}
            <span className="font-semibold">{formatTime(resetPoint)}</span>
          </span>
          <span className="text-right">{formatTime(duration)}</span>
        </div>
      </div>
    </div>;
};